/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2020-2021, Bulb Inc. For more information, please email contact@hellobulb.com.
 *          _
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
*/

import { Inject, Injectable } from '@angular/core';
import { AccountFeatureFlagRouter } from './models/account-feature-flag-router.model';
import { FeatureFlagRouter } from './interfaces/feature-flag-router.interface';

/**
 * Angular service that handles feature availability.
 */
@Injectable()
export class FeatureFlagService {
  /**
   * FeatureFlag enum.
   */
  static FeatureFlag = AccountFeatureFlagRouter.FeatureFlag;

  /**
   * Map of featureNames to FeatureFlagRouters which can route feature flag requests.
   */
  private featureFlagRouterMap : Map< string, FeatureFlagRouter > = new Map< string, FeatureFlagRouter >();

  /**
   * Array of the all potential FeatureFlagRouters.
   */
  private featureFlagRouters : Array< FeatureFlagRouter > = new Array< FeatureFlagRouter >();

  /**
   * Default Constructor.
   */
  constructor(@Inject('BulbAuthService') private bulbAuthService) {
    // TODO FUTURE : implement a service that fetches all potential FeatureFlagRouters.
    // Need to add the potential FeatureFlagRouter types here. This is manual process for now.
    this.featureFlagRouters.push(new AccountFeatureFlagRouter(this.bulbAuthService.currentUser));

    for (const router of this.featureFlagRouters) {
      for (const featureName of router.getRoutedFeatureNames()) {
        this.featureFlagRouterMap.set(featureName, router);
      }
    }
  }

  /**
   * Method that checks if a specific feature is enabled or not.
   *
   * @param featureName Name of the feature we want to check if enabled.
   * @return boolean Whether or not the feature is enabled for the current user.
   */
  isFeatureFlagEnabled(featureName: string) : boolean {
    const router : FeatureFlagRouter = this.featureFlagRouterMap.get(featureName);
    if (!router) {
      return false;
    }
    return router.isFeatureFlagEnabled(featureName);
  }
}
